import { concatBytes } from '@casa/util';

export const NalType = {
    Slice: 1,
    Idr: 5,
    Sei: 6,
    Sps: 7,
    Pps: 8,
    AccessUnitDelimiter: 9,
    StapA: 24,
    FuA: 28,
} as const;

export const nalType = (nal: Uint8Array) : number => (nal[0] ?? 0) & 0x1f;

export interface SpsInfo {
    profileIdc: number;
    constraintFlags: number;
    levelIdc: number;
    /** Picture size after cropping. */
    width: number;
    height: number;
    /** WebCodecs codec string, e.g. `avc1.4d401f`. */
    codec: string;
}

/** Removes the emulation prevention bytes (the 03 in 00 00 03) from a NAL unit payload. */
export function unescapeRbsp(bytes: Uint8Array) : Uint8Array {
    const result = new Uint8Array(bytes.length);
    let length = 0;
    let zeros = 0;
    for (const byte of bytes) {
        if (zeros >= 2 && byte === 3) {
            zeros = 0;
            continue;
        }
        zeros = byte === 0 ? zeros + 1 : 0;
        result[length++] = byte;
    }
    return result.subarray(0, length);
}

class BitReader {
    #position = 0;
    readonly #bytes: Uint8Array;

    constructor(bytes: Uint8Array) {
        this.#bytes = bytes;
    }

    bit() : number {
        const byte = this.#bytes[this.#position >> 3];
        if (byte === undefined) {
            throw new RangeError('read past the end of the NAL unit');
        }

        return (byte >> (7 - (this.#position++ & 7))) & 1;
    }

    bits(count: number) : number {
        let value = 0;
        for (let i = 0; i < count; i++) {
            value = value * 2 + this.bit();
        }
        return value;
    }

    /** Unsigned Exp-Golomb. */
    ue() : number {
        let zeros = 0;
        while (this.bit() === 0) {
            if (++zeros > 32) {
                throw new RangeError('invalid Exp-Golomb code');
            }
        }
        return 2 ** zeros - 1 + this.bits(zeros);
    }

    /** Signed Exp-Golomb. */
    se() : number {
        const value = this.ue();

        return value % 2 === 0 ? -(value / 2) : (value + 1) / 2;
    }
}

function skipScalingList(reader: BitReader, size: number) : void {
    let last = 8;
    let next = 8;
    for (let i = 0; i < size; i++) {
        if (next !== 0) {
            next = (last + reader.se() + 256) % 256;
        }
        if (next !== 0) {
            last = next;
        }
    }
}

// Profiles whose SPS carries the chroma format and scaling fields (H.264 §7.3.2.1.1).
const HIGH_PROFILES: ReadonlySet<number> = new Set([100, 110, 122, 244, 44, 83, 86, 118, 128, 138, 139, 134, 135]);

const hex2 = (value: number) : string => value.toString(16).padStart(2, '0');

/** Reads profile, level and picture size from a sequence parameter set (NAL header included). */
export function parseSps(nal: Uint8Array) : SpsInfo {
    if (nalType(nal) !== NalType.Sps) {
        throw new Error('not a sequence parameter set');
    }

    const reader = new BitReader(unescapeRbsp(nal.subarray(1)));
    const profileIdc = reader.bits(8);
    const constraintFlags = reader.bits(8);
    const levelIdc = reader.bits(8);
    reader.ue(); // seq_parameter_set_id

    let chromaFormatIdc = 1;
    let separateColourPlane = 0;
    if (HIGH_PROFILES.has(profileIdc)) {
        chromaFormatIdc = reader.ue();
        if (chromaFormatIdc === 3) {
            separateColourPlane = reader.bit();
        }
        reader.ue(); // bit_depth_luma_minus8
        reader.ue(); // bit_depth_chroma_minus8
        reader.bit(); // qpprime_y_zero_transform_bypass_flag
        if (reader.bit()) {
            const lists = chromaFormatIdc === 3 ? 12 : 8;
            for (let i = 0; i < lists; i++) {
                if (reader.bit()) {
                    skipScalingList(reader, i < 6 ? 16 : 64);
                }
            }
        }
    }

    reader.ue(); // log2_max_frame_num_minus4
    const pocType = reader.ue();
    if (pocType === 0) {
        reader.ue(); // log2_max_pic_order_cnt_lsb_minus4
    } else if (pocType === 1) {
        reader.bit(); // delta_pic_order_always_zero_flag
        reader.se(); // offset_for_non_ref_pic
        reader.se(); // offset_for_top_to_bottom_field
        const cycle = reader.ue();
        for (let i = 0; i < cycle; i++) {
            reader.se();
        }
    }
    reader.ue(); // max_num_ref_frames
    reader.bit(); // gaps_in_frame_num_value_allowed_flag
    const widthInMbs = reader.ue() + 1;
    const heightInMapUnits = reader.ue() + 1;
    const frameMbsOnly = reader.bit();
    if (!frameMbsOnly) {
        reader.bit(); // mb_adaptive_frame_field_flag
    }
    reader.bit(); // direct_8x8_inference_flag

    let cropLeft = 0;
    let cropRight = 0;
    let cropTop = 0;
    let cropBottom = 0;
    if (reader.bit()) {
        cropLeft = reader.ue();
        cropRight = reader.ue();
        cropTop = reader.ue();
        cropBottom = reader.ue();
    }
    const chromaArrayType = separateColourPlane ? 0 : chromaFormatIdc;
    const cropUnitX = chromaArrayType === 1 || chromaArrayType === 2 ? 2 : 1;
    const cropUnitY = (chromaArrayType === 1 ? 2 : 1) * (2 - frameMbsOnly);

    return {
        profileIdc,
        constraintFlags,
        levelIdc,
        width: widthInMbs * 16 - cropUnitX * (cropLeft + cropRight),
        height: (2 - frameMbsOnly) * heightInMapUnits * 16 - cropUnitY * (cropTop + cropBottom),
        codec: `avc1.${hex2(profileIdc)}${hex2(constraintFlags)}${hex2(levelIdc)}`,
    };
}

const START_CODE = Uint8Array.of(0, 0, 0, 1);

/** Joins NAL units into an Annex B byte stream (each prefixed with a start code). */
export function toAnnexB(nalus: readonly Uint8Array[]) : Uint8Array {
    return concatBytes(nalus.flatMap((nal) => [START_CODE, nal]));
}
